import {useState, useRef, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import {validate} from '../../../../utils/validateImageFile'

import {formatError, convertHEICToJPEG} from '../../../../utils'

import {update, showAlert} from '../../../../actions'

import {useIsMounted} from '../../../../hooks'

import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'

import EditIcon from '@mui/icons-material/Edit'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'

import Image from '../../Image'
import ThreeDotProgress from '../../../generics/ThreeDotProgress'


const styles = {
  uploadZone: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed #ccc;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    position: relative;
    cursor: pointer;
    transition: background 200ms, border-color 200ms;
    height: 100%;
    background: #F6F6F6;
    &:hover {
      background: rgb(247, 248, 255);
      border: 2px solid #3ea5ff;
    }
    &[data-drag-active='true'] {
      background: rgba(62,166,255,0.1);
      border-color: rgb(62,166,255);
    }
    @media (max-width: 400px) {
      padding: 16px;
    }
  `,
  previewImg: css`
    width: 100%;
    border-radius: 8px;
    object-fit: cover;
  `,
  helperText: css`
    font-size: 0.9rem;
    color: #484848;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 320px;
    font-weight: 500;

    p {
      line-height: 1.2;
    }
  `,
  errorText: css`
    color: #f44336;
    font-size: 0.85rem;
    margin-top: 8px;
  `,
  badge: css`
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
  `,
  button: css`
    background: #fff;
    margin-top: 15px;
    margin-bottom: 15px;
    height: 42px;
    width: 130px;
    font-size: 0.9rem;
    font-weight: 500;
  `,
  cameraIcon: css`
    font-size: 36px;
  `
}


// This is designed to work with Formik
const ImageField = ({
  fieldName = 'image',
  fieldValue,
  image,
  imageId,
  imageType,
  callback,
  update,
  showAlert,
  placeholder,
  setFieldValue,
  setFieldError
}) => {
  const inputRef = useRef()
  
  const isMounted = useIsMounted()
  
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const previewSrc = (
    fieldValue instanceof File
      ? URL.createObjectURL(fieldValue)
      : fieldValue || image
  )


  const processFile = useCallback(async file => {
    setError(null)
    setLoading(true)
    let finalFile = file

    if (/image\/heic|image\/heif/.test(file.type)) {
      try {
        finalFile = await convertHEICToJPEG(file)
      } catch (err) {
        setLoading(false)
        setError('Error converting HEIC → JPEG')
        return showAlert('Error converting HEIC to JPEG', 'error')
      }
    }

    const validation = validate(finalFile)
    if (!validation.passed) {
      setLoading(false)
      setError(validation.message)
      setFieldError(fieldName, validation.message)
      return showAlert(validation.message, 'error')
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (!isMounted.current) return
      callback?.(reader.result, finalFile)
      setLoading(false)

      if (imageId) {
        const fd = new FormData()
        fd.append('image', finalFile, finalFile.name)
        update(`/images/${imageId}`, imageType, fd)
          .then(() => showAlert('Image uploaded', 'success'))
          .catch(err => {
            const msg = formatError(err).messages?.image || 'Upload failed'
            setError(msg)
            showAlert(msg, 'error')
          })
      } else {
        setFieldValue(fieldName, finalFile)
      }
    }
    reader.readAsDataURL(finalFile)
  }, [
    callback, 
    fieldName, 
    imageId, 
    imageType, 
    isMounted, 
    setFieldError,
    setFieldValue, 
    showAlert, 
    update
  ])


  const handleFiles = useCallback(
    filesList => {
      if (loading) return
      const file = filesList[0]
      if (file) processFile(file)
    },
    [loading, processFile]
  )


  const onSelect = e => {
    handleFiles(e.target.files)
    e.target.value = ''
  }


  const onDrop = e => {
    e.preventDefault()
    setDragActive(false)
    handleFiles(e.dataTransfer.files)
  }


  const onDragOver = e => {
    e.preventDefault()
    setDragActive(true)
  }


  const onDragLeave = e => {
    e.preventDefault()
    setDragActive(false)
  }


  const triggerFile = e => {
    e.stopPropagation()
    if (!loading) inputRef.current.click()
  }



  return (
    <>
      <div
        css={styles.uploadZone}
        role='button'
        aria-label='Upload image'
        tabIndex={0}
        onClick={triggerFile}
        onKeyPress={e => (e.key === 'Enter' && triggerFile(e))}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-drag-active={dragActive ? 'true' : 'false'}
      >
        {previewSrc ? (
          <Image
            css={styles.previewImg}
            src={previewSrc}
            alt='preview'
            placeholder={placeholder}
          />
        ) : (
          <>
            <p css={styles.cameraIcon}>
              📷
            </p>
            <Button
              css={styles.button}
              variant='outlined'
              onClick={triggerFile}
              disabled={loading}
              sx={{
                color: 'dark-grey',
                border: '2px solid #D3D3D3',
                borderRadius: '8px',     
                '&:hover': {
                  border: '2px solid #D3D3D3',
                  background: '#fff'
                }
              }}
            >
              Upload image
            </Button>
            <div css={styles.helperText}>
              <p>File formats: .png, .jpg, .webp, .heic</p>
              
              <p>Max. file size: 10 MB</p>

              <p>
                Event images use a 2:3 aspect ratio to resemble concert posters.
                Most other images will have a square aspect ratio.
              </p>
            </div>
          </>
        )}

        <input
          ref={inputRef}
          type='file'
          accept='image/png,image/jpeg,image/heic,image/webp'
          style={{display: 'none'}}
          onChange={onSelect}
        />

        {loading && <ThreeDotProgress position='absolute' radius={4} />}
      </div>

      {error && <div css={styles.errorText}>{error}</div>}
    </>
  )
}


export default connect(null, {update, showAlert})(ImageField)







